import { useRef } from "react"
import FlowerTable from "./components/ThreeFlowers"
import Flower from "./components/Flower"
import MainBranch from "./components/MainBranch"
import {
  TREE_SCALE,
  typeFlowerColors,
  calculateRequiredBranches,
  generateCompleteTreeStructure,
  calculateTreeDimensions,
  getDynamicBranchStyles,
} from "./constants/constants"

export const SIM_MAX_FLOWERS = 7 + 6 + 6
export const SIM_MAX_LEAVES = 54

export const SIM_NATIVE_WIDTH = 450 * TREE_SCALE

const WATERMARK_FONT_SIZE = 32
const WATERMARK_TOP = -10
const WATERMARK_LETTER_SPACING = 6
const TREE_BOTTOM_OFFSET = 20

function isFlowerSlotVisible(flowerId, slotMap) {
    const idx = parseInt(flowerId.replace("f", "")) - 1
    return idx < slotMap.length && slotMap[idx] !== null
}

function applySlotMapVisibility(branches, slotMap) {
    return branches.map((branch) => {
        const patched = { ...branch }
        patched.flowerVisible = isFlowerSlotVisible(patched.flowerId, slotMap)
        if (patched.innerBranches) {
            patched.innerBranches = patched.innerBranches.map((inner) => {
                const patchedInner = { ...inner }
                patchedInner.flowerVisible = isFlowerSlotVisible(patchedInner.flowerId, slotMap)
                if (patchedInner.subBranch) {
                    patchedInner.subBranch = { ...patchedInner.subBranch }
                    patchedInner.subBranch.flowerVisible = isFlowerSlotVisible(patchedInner.subBranch.flowerId, slotMap)
                }
                return patchedInner
            })
        }
        return patched
    })
}

function stripToBranchesOnly(branches) {
    return branches.map((branch) => {
        const b = { ...branch, leaf1Visible: false, leaf2Visible: false, leaf3Visible: false, flowerVisible: false, visible: true }
        if (b.innerBranches) {
            b.innerBranches = b.innerBranches.map((inner) => {
                const i = { ...inner, leaf1Visible: false, leaf2Visible: false, leaf3Visible: false, flowerVisible: false, visible: true }
                if (i.subBranch) i.subBranch = { ...i.subBranch, leaf1Visible: false, flowerVisible: false, visible: true }
                return i
            })
        }
        return b
    })
}

function nativeDimensions() {
    const { branchCount } = calculateRequiredBranches(SIM_MAX_LEAVES, SIM_MAX_FLOWERS)
    const dimensions = calculateTreeDimensions(branchCount)
    return { branchCount, dimensions, nativeHeight: Math.round(dimensions.backgroundHeight * TREE_SCALE) }
}

export function getSimulatedTreeHeight() {
    return nativeDimensions().nativeHeight
}

export default function SimulatedTalkTreePreview({
    totalLeaves,
    slotMap,
    sessionId = "talk-tree-simulator",
    skeleton = false,
    showLegend = true,
    targetHeight = null,
    watermarkText = null,
    scale = 1,
}) {
    const containerRef = useRef(null)
    const getFlowerColor = (flowerId) => {
        if (skeleton) return "whiteflower"
        const idx = parseInt(flowerId.replace("f", "")) - 1
        const type = slotMap[idx]
        if (!type) return "whiteflower"
        return typeFlowerColors[type] || "whiteflower"
    }
    const shouldShowLeaf = (leafNum) => leafNum <= totalLeaves
    const { branchCount, dimensions, nativeHeight } = nativeDimensions()
    const completeTreeStructureData = generateCompleteTreeStructure(SIM_MAX_FLOWERS, totalLeaves, branchCount)
    const patchedBranches = skeleton
        ? stripToBranchesOnly(completeTreeStructureData.branches)
        : applySlotMapVisibility(completeTreeStructureData.branches, slotMap)

    const nativeWidth = SIM_NATIVE_WIDTH
    const nativeBoxHeight = (targetHeight ?? nativeHeight) + TREE_BOTTOM_OFFSET

    const boxWidth = nativeWidth * scale
    const boxHeight = nativeBoxHeight * scale

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ position: "relative", width: `${boxWidth}px`, height: `${boxHeight}px` }}>

                <div
                    style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: `${nativeWidth}px`,
                        height: `${nativeBoxHeight}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: "bottom left",
                    }}
                >

                    {watermarkText && (
                        <div
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                top: `${WATERMARK_TOP}px`,
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: `${WATERMARK_FONT_SIZE}px`,
                                fontWeight: "bold",
                                color: "rgba(226, 223, 223, 0.4)",
                                textTransform: "uppercase",
                                letterSpacing: `${WATERMARK_LETTER_SPACING}px`,
                                textShadow: "0 0 2px rgba(173, 168, 168, 0.5)",
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                userSelect: "none",
                                zIndex: 0,
                            }}
                        >
                            {watermarkText}
                        </div>
                    )}

                    <div
                        className="tree-container"
                        aria-label="Talk tree preview"
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            height: `${nativeHeight}px`,
                            width: `${nativeWidth}px`,
                            opacity: skeleton ? 0.5 : 1,
                            transition: "opacity 0.35s ease",
                        }}
                    >
                        <div className="tree-scale-wrapper" style={{ position: "absolute", bottom: 0, left: "50%", transform: `translateX(-50%) scale(${TREE_SCALE})`, transformOrigin: "bottom center", zIndex: 1 }}>
                            <div className="tree-display" data-session-id={sessionId} role="img" aria-label="Generated talk tree preview">
                                <div ref={containerRef} className="background" style={{ height: `${dimensions.backgroundHeight}px`, width: "350px", overflow: "visible" }}>
                                    <div className="slope" aria-hidden="true" />
                                    <div className="tree" aria-hidden="true" style={{ "--trunk-height": `${dimensions.trunkHeight}px` }}>
                                        {[1, 2, 3].map(
                                            (leafNum) =>
                                                !skeleton && shouldShowLeaf(leafNum) && <div key={`leaf-${leafNum}`} style={{ display: "block" }} className={`leaf leaf${leafNum}`} aria-hidden="true" />
                                        )}
                                        {!skeleton && isFlowerSlotVisible("f1", slotMap) && <Flower flowerId="f1" color={getFlowerColor("f1")} />}
                                        {patchedBranches.map((branch) => (
                                            <MainBranch
                                                key={branch.id}
                                                {...branch}
                                                getFlowerColor={getFlowerColor}
                                                style={branch.isDynamic ? getDynamicBranchStyles(branch.branchNum, dimensions.trunkHeight) : undefined}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showLegend && (
                <div style={{ marginTop: "16px", width: "100%", display: "flex", justifyContent: "center" }}>
                    <FlowerTable isPresentationPage />
                </div>
            )}
        </div>
    )
}